import { useCallback, useMemo, useState } from 'react';
import { Typography, SearchInput } from 'app/components/ui';
import { TagListItem } from './TagListItem';
import { ChainGroup } from './ChainGroup';
import { groupTagsIntoChains } from './groupChains';
import { sortTagsByDocumentOrder } from './documentOrder';
import { i18n } from 'app/utils/i18n';
import { TagWithMetadata, useTagsByDocument } from 'app/api/tag-queries';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { Spinner } from 'app/components';
import { FilterDropdown, useFilteredTags, FilterResults, FilterGroup } from '../TagsFilter';

import './AllTags.scss';

interface AllTagsProps {
  selectedTag: TagWithMetadata | null;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onExtensionClick?: (tagId: string) => void;
  onDeleteClick?: (tag: TagWithMetadata) => void;
  filterTagIds?: string[] | null;
}

export const AllTags = ({ selectedTag, onSelectTag, onTagDetailsClick, onExtensionClick, onDeleteClick, filterTagIds }: AllTagsProps) => {
  const { pDocument, section } = useEditorSelector((editor: any) => editor);
  const { data: tagsResponse, isLoading } = useTagsByDocument(pDocument?.superId, {
    include: ['metadata', 'concept'],
  });
  const tagsData = useMemo(() => tagsResponse?.tags ?? [], [tagsResponse]);

  const [showCurrentSectionOnly, setShowCurrentSectionOnly] = useState(false);
  const currentSectionId = section?.superId || null;

  const sectionFilter = useCallback(
    (tag: TagWithMetadata) => !showCurrentSectionOnly || tag.metadata?.sectionSuperId === currentSectionId,
    [showCurrentSectionOnly, currentSectionId]
  );

  const filterGroups: FilterGroup[] = [
    {
      label: i18n('tag.highlighted.option.status'),
      value: 'status',
      icon: <i className="fa-solid fa-spinner" />,
      options: [
        { label: i18n('tag.status.approved'), value: 'approved' },
        { label: i18n('tag.status.draft'), value: 'draft' },
        { label: i18n('tag.status.need_attention'), value: 'need_attention' },
        { label: i18n('tag.status.verified'), value: 'verified' },
      ],
    },
    {
      label: i18n('tag.validation.label'),
      value: 'validated',
      icon: <i className="fa-solid fa-flag" />,
      options: [
        { label: i18n('tag.status.invalid'), value: 'invalid' },
        { label: i18n('tag.status.unvalidated'), value: 'unvalidated' },
        { label: i18n('tag.status.valid'), value: 'valid' },
      ],
    },
    {
      label: i18n('tag.type'),
      value: 'type',
      icon: <i className="fa-solid fa-tags" />,
      options: [
        { label: i18n('tag.type.numeric'), value: 'nonFraction' },
        { label: i18n('tag.type.textBlock'), value: 'nonNumeric' },
        { label: i18n('tag.type.fraction'), value: 'fraction' },
      ],
    },
  ];

  const { filters, filteredTags, onAddFilter, onRemoveFilter, searchInput, setSearchInput } = useFilteredTags(
    tagsData,
    filterGroups,
    filterTagIds,
    null,
    sectionFilter
  );

  const orderedTags = useMemo(() => sortTagsByDocumentOrder(filteredTags), [filteredTags]);

  const listEntries = useMemo(() => groupTagsIntoChains(orderedTags, tagsData), [orderedTags, tagsData]);

  return (
    <section className="all-tags">
      <div className="all-tags__header">
        <div className="all-tags__header--search-row">
          <SearchInput searchInput={searchInput} setSearchInput={setSearchInput} />
          <FilterDropdown iconOnly onSelect={onAddFilter} selected={filters} groups={filterGroups} />
        </div>

        <div className="all-tags__header--filter-row">
          <span className="all-tags__header--checkbox-label">
            <input className="fs-3" type="checkbox" checked={showCurrentSectionOnly} onChange={() => setShowCurrentSectionOnly((prev) => !prev)} />
            <Typography variant="body-5" fontWeight="extra-light">
              {i18n('all.tags.show.current.section')}
            </Typography>
          </span>

          <Typography variant="body-3" fontWeight="extra-light">
            {`${filteredTags.length} ${i18n('all.tags.of')} ${tagsData?.length ?? 0} ${i18n('all.tags.tags')}`}
          </Typography>
        </div>

        <FilterResults filters={filters} onRemove={onRemoveFilter} />
      </div>

      <div className="all-tags__content">
        {isLoading ? (
          <>
            <Typography>{i18n('all.tags.loading')}</Typography>
            <Spinner />
          </>
        ) : filteredTags.length > 0 ? (
          <ul className="all-tags__list">
            {listEntries.map((entry) =>
              entry.kind === 'chain' ? (
                <ChainGroup
                  key={entry.rootId}
                  tags={entry.tags}
                  selectedTag={selectedTag}
                  onSelectTag={onSelectTag}
                  onTagDetailsClick={onTagDetailsClick}
                  onExtensionClick={onExtensionClick}
                  onDeleteClick={onDeleteClick}
                />
              ) : (
                <TagListItem
                  key={entry.tag.id}
                  tag={entry.tag}
                  selectedTag={selectedTag}
                  onSelectTag={onSelectTag}
                  onTagDetailsClick={onTagDetailsClick}
                  onExtensionClick={onExtensionClick}
                  onDeleteClick={onDeleteClick}
                />
              )
            )}
          </ul>
        ) : (
          <Typography>{i18n('all.tags.empty.list')}</Typography>
        )}
      </div>
    </section>
  );
};
